import React, { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { find } from 'lodash';
import TeamNavigation, { Customer, TabEnum, TeamNavApplication, TeamNavTab } from '@lendi/team-navigation';

import { TEAM_VIEW_URLS, URL_PARAM_APPLICATION_ID, URL_PARAM_CUSTOMER_ID } from '@funnels/constants';
import { CustomersProvider } from '@funnels/providers';
import { ApplicantsAPI, ApplicationsAPI, CustomersAPI } from '@funnels/redux-store';
import { CustomerData, StoreApplicant, StoreApplication, UseParams } from '@funnels/types';
import { apiUtil, datadogUtil } from '@funnels/utils';

interface TeamViewNavWrapperProps {
  children: React.ReactNode;
  teamNavTab?: TeamNavTab;
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Functions used to emulate Promise.allSettled, which doesn't yet have stable cross browser support         //
// ref: https://stackoverflow.com/questions/31424561/wait-until-all-promises-complete-even-if-some-rejected //
//////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface CustomerPromiseResult {
  payload: any;
  resolved: boolean;
}
const catchHandler = (error: any) => {
  datadogUtil.addError(error);
  return { payload: error, resolved: false };
};
const successHandler = (result: any) => ({ payload: result, resolved: true });
const reflect = (p: any) => p.then(successHandler).catch(catchHandler);
//////////////////////////////////////////////////////////////////////////////////////////////////////////////

const emptyTeamNavApplication = { id: '', name: 'New application' };

export function TeamNav(props: TeamViewNavWrapperProps) {
  const token = apiUtil.getToken();
  const { [URL_PARAM_APPLICATION_ID]: urlParamApplicationId, [URL_PARAM_CUSTOMER_ID]: urlParamCustomerId } =
    useParams<UseParams>();
  const [activeApplication, setActiveApplication] = React.useState<TeamNavApplication>(emptyTeamNavApplication);
  const [, setActiveCustomer] = React.useState<Customer | undefined>(undefined);
  const [applications, setApplications] = React.useState<StoreApplication[]>([]);
  const [customers, setCustomers] = React.useState<CustomerData[]>([]);
  const location = useLocation();

  useEffect(() => {
    if (!urlParamCustomerId || !token) {
      return;
    }

    if (!urlParamApplicationId) {
      CustomersAPI.fetch(urlParamCustomerId)
        .then((res) => res.data)
        .then((customer) => {
          setCustomers([customer]);
        });
    }

    ApplicationsAPI.fetchAll(urlParamCustomerId).then((res) => {
      setApplications(res.data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const selectedApplication = find(applications, (a) => a.id === urlParamApplicationId);
    const teamNavApplication = selectedApplication
      ? { id: selectedApplication.id || '', name: selectedApplication.name || '' }
      : emptyTeamNavApplication;
    setActiveApplication(teamNavApplication);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications, urlParamApplicationId]);

  // Fetch all the customers for the application
  useEffect(() => {
    if (!activeApplication.id) {
      return;
    }

    ApplicantsAPI.fetch(activeApplication.id).then((res) => {
      const customerIds = res.data
        .map((a: StoreApplicant) => a.customerId)
        .filter((id: string | undefined) => !!id) as string[];
      const customerPromises = customerIds.map((id) => CustomersAPI.fetch(id).then((res) => res.data));
      Promise.all(customerPromises.map(reflect)).then((res: CustomerPromiseResult[]) => {
        const resolvedCustomers = res.filter((c) => c.resolved).map((c) => ({ ...c.payload }));
        setCustomers(resolvedCustomers);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeApplication]);

  useEffect(() => {
    const selectedCustomer = find(customers, (c) => c.id === urlParamCustomerId);
    const teamNavCustomer = selectedCustomer
      ? {
          customerId: selectedCustomer.id || '',
          email: selectedCustomer.email || '',
          name: selectedCustomer.firstName || '',
          phone: selectedCustomer.mobileNumber || '',
        }
      : undefined;
    setActiveCustomer(teamNavCustomer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, urlParamCustomerId]);

  // not display TeamNavigation in application creating page
  if (location.pathname.includes(TEAM_VIEW_URLS.NEW_APPLICATION)) {
    return <CustomersProvider customers={customers}>{props.children}</CustomersProvider>;
  }

  return (
    <TeamNavigation
      applicationId={urlParamApplicationId}
      activeTab={props.teamNavTab || TabEnum.PROPERTY}
      onException={(err: Error) => datadogUtil.addError(err)}
    >
      <CustomersProvider customers={customers}>{props.children}</CustomersProvider>
    </TeamNavigation>
  );
}
